import React from 'react';
import { cn } from 'src/domains/common/components/utils';
import { observer } from 'src/utils/mobx-react';

type TableRowPropsType = React.HTMLAttributes<HTMLTableRowElement> & {
    dataTest?: string;
};

export const Table = observer('Table', ({ children, className, ...props }: React.HTMLAttributes<HTMLTableElement>) => {
    const isTableFixed = (className ?? '').includes('table-fixed');
    return (
        <table
            className={cn('caption-bottom text-sm', !isTableFixed && 'w-full', className)}
            {...props}
        >
            {children}
        </table>
    );
});

export const TableHeader = observer(
    'TableHeader',
    ({ children, className, ...props }: React.HTMLAttributes<HTMLTableSectionElement>) => {
        return (
            <thead
                className={cn('[&_tr]:border-b', className)}
                {...props}
            >
                {children}
            </thead>
        );
    }
);

export const TableBody = observer(
    'TableBody',
    ({ children, className, ...props }: React.HTMLAttributes<HTMLTableSectionElement>) => {
        return (
            <tbody
                className={cn('[&_tr:last-child]:border-0', className)}
                {...props}
            >
                {children}
            </tbody>
        );
    }
);

export const TableFooter = observer(
    'TableFooter',
    ({ children, className, ...props }: React.HTMLAttributes<HTMLTableSectionElement>) => {
        return (
            <tfoot
                className={cn('border-t bg-muted/50 font-medium [&>tr]:last:border-b-0', className)}
                {...props}
            >
                {children}
            </tfoot>
        );
    }
);

export const TableRow = observer('TableRow', ({ children, dataTest, className, ...props }: TableRowPropsType) => {
    return (
        <tr
            className={cn('border-b transition-colors data-[state=selected]:bg-muted', className)}
            data-test={dataTest}
            {...props}
        >
            {children}
        </tr>
    );
});

export const TableHead = observer(
    'TableHead',
    ({ children, className, ...props }: React.ThHTMLAttributes<HTMLTableCellElement>) => {
        return (
            <th
                className={cn(
                    'px-2 text-left align-middle font-medium text-muted-foreground whitespace-nowrap [&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:translate-y-[2px]',
                    className
                )}
                {...props}
            >
                {children}
            </th>
        );
    }
);

export const TableCell = observer(
    'TableCell',
    ({ children, className, ...props }: React.TdHTMLAttributes<HTMLTableCellElement>) => {
        return (
            <td
                className={cn(
                    'p-2 text-foreground text-xs align-middle overflow-hidden [&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:translate-y-[2px]',
                    className
                )}
                {...props}
            >
                {children}
            </td>
        );
    }
);

export const TableCaption = observer(
    'TableCaption',
    ({ children, className, ...props }: React.HTMLAttributes<HTMLTableCaptionElement>) => {
        return (
            <caption
                className={cn('mt-4 text-sm text-muted-foreground', className)}
                {...props}
            >
                {children}
            </caption>
        );
    }
);
